import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useDynamicColors from "../../hooks/useDynamicColors";
import useClientName from "../../hooks/useClientName";
import "./style.css";

const mockUsers = [
  { id: 1, photo: "/img/frame-480954771-5.png", name: "Ana Juliana Hurtado", email: "ana.hurtado@email.com", phone: "+1 555-0101", status: "Approved", updated: "2025-01-15" },
  { id: 2, photo: null, name: "Carlos Martinez", email: "carlos.m@email.com", phone: "+1 555-0102", status: "Approved", updated: "2025-01-14" },
  { id: 3, photo: null, name: "Maria Rodriguez", email: "maria.r@email.com", phone: "+1 555-0103", status: "In Process", updated: "2025-01-13" },
  { id: 4, photo: null, name: "James Wilson", email: "james.w@email.com", phone: "+1 555-0104", status: "Rejected", updated: "2025-01-12" },
  { id: 5, photo: null, name: "Sofia Petrov", email: "sofia.p@email.com", phone: "+1 555-0105", status: "Approved", updated: "2025-01-11" },
];

const navItems = [
  { label: "Home", icon: "home" },
  { label: "Users", icon: "users", active: true },
  { label: "Merchants", icon: "merchants" },
  { label: "Campaigns", icon: "campaigns" },
  { label: "Audits", icon: "audits" },
];

const NavIcon = ({ icon, color }) => {
  const icons = {
    home: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
    users: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    merchants: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
      </svg>
    ),
    campaigns: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    ),
    audits: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    ),
  };
  return icons[icon] || null;
};

const StatusBadge = ({ status }) => {
  const config = {
    Approved: { bg: "#DEF7EC", color: "#03543F" },
    "In Process": { bg: "#FEF3C7", color: "#92400E" },
    Rejected: { bg: "#FDE8E8", color: "#9B1C1C" },
  };
  const s = config[status] || config.Approved;
  return (
    <span
      style={{
        backgroundColor: s.bg,
        color: s.color,
        padding: "2px 10px",
        borderRadius: "12px",
        fontSize: "0.65rem",
        fontWeight: 500,
      }}
    >
      {status}
    </span>
  );
};

const Avatar = ({ src, name }) => {
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        style={{
          width: "28px",
          height: "28px",
          borderRadius: "50%",
          objectFit: "cover",
        }}
      />
    );
  }
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();
  return (
    <div
      style={{
        width: "28px",
        height: "28px",
        borderRadius: "50%",
        backgroundColor: "#E5E7EB",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.55rem",
        fontWeight: 600,
        color: "#6B7280",
      }}
    >
      {initials}
    </div>
  );
};

export const UserDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const clientName = useClientName();
  const colors = useDynamicColors();
  const [filtersOpen, setFiltersOpen] = useState(false);

  const handleUserClick = () => {
    const currentParams = location.search;
    navigate(`/backoffice-user_details-${clientName}-web${currentParams}`);
  };

  return (
    <div style={styles.laptop}>
      <div style={styles.screen}>
        <div style={styles.layout}>
          {/* Left Sidebar */}
          <div style={styles.sidebar}>
            {/* Logo */}
            <div style={{ textAlign: "center", padding: "16px 12px 8px" }}>
              <img
                src={
                  colors.url_logo ||
                  "https://vxapp.wfsystems.com.ar/storage/logos/20240416192440.png"
                }
                alt="Logo"
                style={{ width: "48px", height: "auto" }}
              />
              <div
                style={{
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  color: colors.primario,
                  marginTop: "6px",
                }}
              >
                VXApp
              </div>
            </div>

            {/* Nav items */}
            <nav style={{ padding: "8px", display: "flex", flexDirection: "column", gap: "2px" }}>
              {navItems.map((item) => (
                <div
                  key={item.label}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px 12px",
                    borderRadius: "6px",
                    fontSize: "0.7rem",
                    fontWeight: item.active ? 600 : 400,
                    color: item.active ? colors.primario : "#6B7280",
                    backgroundColor: item.active
                      ? `${colors.primario}15`
                      : "transparent",
                    cursor: "pointer",
                  }}
                >
                  <NavIcon icon={item.icon} color={item.active ? colors.primario : colors.secundario} />
                  {item.label}
                </div>
              ))}
            </nav>
          </div>

          {/* Main area */}
          <div style={styles.mainArea}>
            {/* Top header bar */}
            <div
              style={{
                ...styles.headerBar,
                backgroundColor: colors.secundario,
              }}
            >
              <span style={{ fontWeight: 600, fontSize: "0.8rem" }}>Users</span>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ textAlign: "right", lineHeight: 1.2 }}>
                  <div style={{ fontSize: "0.65rem", opacity: 0.8 }}>User</div>
                  <div style={{ fontSize: "0.7rem", fontWeight: 600 }}>Admin</div>
                </div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </div>
            </div>

            {/* Content area with scroll */}
            <div style={styles.contentScroll}>
              {/* Title section */}
              <div style={{ marginBottom: "12px" }}>
                <h1 style={{ fontSize: "1.1rem", fontWeight: 700, color: "#111827", margin: 0 }}>
                  Users
                </h1>
                <p style={{ fontSize: "0.7rem", color: "#6B7280", margin: "2px 0 0" }}>
                  Manage and monitor users
                </p>
              </div>

              {/* Filters card */}
              <div style={styles.filterCard}>
                <div
                  onClick={() => setFiltersOpen(!filtersOpen)}
                  style={styles.filterToggle}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "0.7rem", fontWeight: 500, color: "#374151" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="4" y1="21" x2="4" y2="14" />
                      <line x1="4" y1="10" x2="4" y2="3" />
                      <line x1="12" y1="21" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12" y2="3" />
                      <line x1="20" y1="21" x2="20" y2="16" />
                      <line x1="20" y1="12" x2="20" y2="3" />
                      <line x1="1" y1="14" x2="7" y2="14" />
                      <line x1="9" y1="8" x2="15" y2="8" />
                      <line x1="17" y1="16" x2="23" y2="16" />
                    </svg>
                    Filters
                  </span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    {filtersOpen ? (
                      <polyline points="18 15 12 9 6 15" />
                    ) : (
                      <polyline points="6 9 12 15 18 9" />
                    )}
                  </svg>
                </div>

                {filtersOpen && (
                  <div style={styles.filterBody}>
                    <div style={styles.filterGrid}>
                      <input placeholder="ID" style={styles.filterInput} />
                      <input placeholder="Name" style={styles.filterInput} />
                      <input placeholder="Email" style={styles.filterInput} />
                      <select style={styles.filterInput}>
                        <option value="">Status</option>
                        <option>Approved</option>
                        <option>In Process</option>
                        <option>Rejected</option>
                      </select>
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "6px", marginTop: "8px" }}>
                      <button
                        style={{
                          ...styles.filterButton,
                          backgroundColor: colors.primario,
                          color: "white",
                        }}
                      >
                        Apply
                      </button>
                      <button style={{ ...styles.filterButton, backgroundColor: "#F3F4F6", color: "#374151" }}>
                        Clear
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Table */}
              <div style={styles.tableCard}>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.65rem" }}>
                    <thead>
                      <tr style={{ backgroundColor: colors.secundario }}>
                        <th style={styles.th}>Photo</th>
                        <th style={styles.th}>Name</th>
                        <th style={styles.th}>Email</th>
                        <th style={styles.th}>Phone</th>
                        <th style={styles.th}>Status</th>
                        <th style={styles.th}>Updated</th>
                        <th style={{ ...styles.th, width: "40px" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mockUsers.map((user) => (
                        <tr key={user.id} style={styles.tr}>
                          <td style={styles.td}>
                            <Avatar src={user.photo} name={user.name} />
                          </td>
                          <td style={{ ...styles.td, fontWeight: 500, color: "#111827" }}>
                            {user.name}
                          </td>
                          <td style={{ ...styles.td, color: "#6B7280" }}>{user.email}</td>
                          <td style={{ ...styles.td, color: "#6B7280" }}>{user.phone}</td>
                          <td style={styles.td}>
                            <StatusBadge status={user.status} />
                          </td>
                          <td style={{ ...styles.td, color: "#9CA3AF" }}>{user.updated}</td>
                          <td style={styles.td}>
                            <div
                              onClick={handleUserClick}
                              style={styles.actionButton}
                              className="highlighted-button"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={colors.primario} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="16" x2="12" y2="12" />
                                <line x1="12" y1="8" x2="12.01" y2="8" />
                              </svg>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div style={styles.pagination}>
                  <span style={{ fontSize: "0.65rem", color: "#6B7280" }}>
                    Showing 1 to 5 of 12
                  </span>
                  <div style={{ display: "flex", gap: "4px" }}>
                    {[1, 2, 3].map((p) => (
                      <button
                        key={p}
                        style={{
                          ...styles.pageButton,
                          backgroundColor: p === 1 ? colors.primario : "#F3F4F6",
                          color: p === 1 ? "white" : "#374151",
                        }}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div style={styles.base}></div>
    </div>
  );
};

const styles = {
  laptop: {
    marginTop: "30px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    backgroundColor: "#333",
    borderRadius: "15px 15px 5px 5px",
    padding: "10px",
    width: "960px",
    height: "500px",
    position: "relative",
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
  },
  screen: {
    backgroundColor: "white",
    borderRadius: "5px",
    overflow: "hidden",
    width: "100%",
    height: "500px",
  },
  layout: {
    display: "flex",
    height: "100%",
    fontFamily: "Inter, sans-serif",
  },
  sidebar: {
    width: "170px",
    minWidth: "170px",
    backgroundColor: "#FFFFFF",
    borderRight: "1px solid #E5E7EB",
    display: "flex",
    flexDirection: "column",
  },
  mainArea: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#F3F4F6",
    overflow: "hidden",
  },
  headerBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 16px",
    color: "white",
  },
  contentScroll: {
    flex: 1,
    overflowY: "auto",
    padding: "16px",
  },
  filterCard: {
    backgroundColor: "white",
    borderRadius: "10px",
    border: "1px solid #E5E7EB",
    marginBottom: "12px",
    overflow: "hidden",
  },
  filterToggle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 14px",
    cursor: "pointer",
  },
  filterBody: {
    borderTop: "1px solid #F3F4F6",
    padding: "10px 14px",
  },
  filterGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr 1fr",
    gap: "8px",
  },
  filterInput: {
    padding: "6px 10px",
    border: "1px solid #D1D5DB",
    borderRadius: "6px",
    fontSize: "0.65rem",
    fontFamily: "Inter, sans-serif",
    outline: "none",
  },
  filterButton: {
    padding: "5px 14px",
    border: "none",
    borderRadius: "6px",
    fontSize: "0.65rem",
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "Inter, sans-serif",
  },
  tableCard: {
    backgroundColor: "white",
    borderRadius: "10px",
    border: "1px solid #E5E7EB",
    overflow: "hidden",
  },
  th: {
    padding: "8px 10px",
    fontWeight: 500,
    color: "white",
    textAlign: "left",
    fontSize: "0.65rem",
  },
  td: {
    padding: "8px 10px",
    fontSize: "0.65rem",
  },
  tr: {
    borderBottom: "1px solid #F3F4F6",
  },
  actionButton: {
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
  },
  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 14px",
    borderTop: "1px solid #F3F4F6",
  },
  pageButton: {
    width: "24px",
    height: "24px",
    border: "none",
    borderRadius: "4px",
    fontSize: "0.6rem",
    fontWeight: 500,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "Inter, sans-serif",
  },
  base: {
    position: "absolute",
    width: "1100px",
    height: "20px",
    backgroundColor: "rgb(170, 170, 170)",
    borderRadius: "0px 0px 10px 10px",
    bottom: 0,
  },
};
